import { useRef } from 'preact/hooks';
import { usePickerSheet } from '../hooks/usePickerSheet';
import styles from './TemplatePickerSheet.module.css';

export interface RoleOption {
  name: string;
  description: string;
}

// Plot Armor (prophecy_fulfillment) Proppian roles. Mirrors
// ProphecyFulfillmentStrategy.PROPPIAN_ROLES on the backend. Descriptions are
// kept plain-language for guest readability — no clinical "Propp morphology"
// jargon. iOS uses TemplateRole records served by /templates; the PWA hardcodes
// here to avoid a templates endpoint roundtrip during recovery.
export const PROPPIAN_ROLE_OPTIONS: RoleOption[] = [
  { name: 'Hero', description: 'Drives the story forward. Acts when no one else will.' },
  { name: 'Villain', description: 'The obstacle. Creates the trouble worth solving.' },
  { name: 'Donor', description: 'Tests the hero and grants the gift that makes the win possible.' },
  { name: 'Helper', description: 'Stands with the hero through the hardest stretch.' },
  { name: 'Princess/Prize', description: 'What the hero is fighting for — a person, a place, a truth.' },
  { name: 'Dispatcher', description: 'Sends the hero on the journey. Names the stakes.' },
  { name: 'False Hero', description: 'Takes credit they didn’t earn. Their fall sharpens the real win.' },
];

interface RolePickerSheetProps {
  open: boolean;
  /**
   * Occupancy per role: role name → how many contributors already hold it.
   * A sphere of action is shareable (Propp Ch. VI), so any number of people can
   * play the same part — occupancy is a gentle "who's here" signal, never a
   * gate. Absent or zero counts mean an empty sphere waiting for a first voice.
   */
  roleOccupancy?: Record<string, number>;
  onSelect: (role: string) => void;
  onDismiss: () => void;
  mode?: 'proactive' | 'recovery';
  errorMessage?: string | null;
  options?: RoleOption[];
  /**
   * Story-Aware Role Options: story-tailored labels keyed by canonical role
   * `name` (e.g. "Hero" → { label: "Seeker" }). When present, the picker shows
   * the tailored label/description but still calls `onSelect(option.name)` with
   * the canonical name. Absent ⇒ canonical labels (today's behavior).
   */
  roleLexicon?: Record<string, { label: string; description?: string }>;
}

export function RolePickerSheet({
  open,
  roleOccupancy = {},
  onSelect,
  onDismiss,
  mode = 'proactive',
  errorMessage,
  options = PROPPIAN_ROLE_OPTIONS,
  roleLexicon,
}: RolePickerSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  usePickerSheet(open, sheetRef, onDismiss);

  if (!open) return null;

  const titleId = 'role-picker-title';

  // Story-tailored display label/description for a role, keyed by canonical
  // `name`. Occupancy + onSelect always stay on the canonical name.
  const labelFor = (opt: RoleOption) => roleLexicon?.[opt.name]?.label ?? opt.name;
  const descriptionFor = (opt: RoleOption) =>
    roleLexicon?.[opt.name]?.description ?? opt.description;

  // Relatedness/gap framing: surface the empty spheres as an open invitation
  // ("No one's playing the Villain yet — want to?"). This is never a block; it
  // simply points to where the story still needs a voice.
  const emptyRoles = options.filter((opt) => (roleOccupancy[opt.name] ?? 0) === 0);
  const gapInvite =
    emptyRoles.length > 0 && emptyRoles.length < options.length
      ? emptyRoles.length === 1
        ? `No one's playing the ${labelFor(emptyRoles[0])} yet — want to bring them to life?`
        : `Some parts are still waiting for a voice — including the ${labelFor(emptyRoles[0])}. Want to take one?`
      : null;

  return (
    <div
      class={`${styles.overlay} ${styles.overlayOpen}`}
      onClick={(event) => {
        if (event.target === event.currentTarget) onDismiss();
      }}
    >
      <div
        ref={sheetRef}
        class={`${styles.sheet} ${styles.sheetOpen}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div class={styles.handle} />
        <div class={styles.header}>
          <h3 id={titleId} class={styles.title}>
            {mode === 'recovery' ? 'Pick a role to keep going' : 'Pick your role'}
          </h3>
          <button
            type="button"
            class={styles.closeButton}
            onClick={onDismiss}
            aria-label="Close"
          >
            &times;
          </button>
        </div>
        <p class={styles.subtitle}>
          {mode === 'recovery'
            ? 'This story uses Plot Armor — every contributor plays a part. Pick yours to post your take. Any part is open, even one someone else is already playing.'
            : 'This story uses Plot Armor — each voice plays a part. Choose how you show up. More than one person can share the same part.'}
        </p>

        {gapInvite && <p class={styles.gapInvite}>{gapInvite}</p>}

        {errorMessage && <div class={styles.error}>{errorMessage}</div>}

        <div class={styles.optionList}>
          {options.map((option) => {
            const occupancy = roleOccupancy[option.name] ?? 0;
            const displayLabel = labelFor(option);
            const occupancyHint =
              occupancy > 0
                ? `${occupancy} ${occupancy === 1 ? 'storyteller' : 'storytellers'} here`
                : null;
            const ariaLabel = occupancyHint
              ? `Select ${displayLabel} role — ${occupancyHint}`
              : `Select ${displayLabel} role — no one here yet, be the first`;
            return (
              <button
                key={option.name}
                type="button"
                class={styles.option}
                onClick={() => onSelect(option.name)}
                aria-label={ariaLabel}
              >
                <span class={styles.optionTitle}>
                  <span>{displayLabel}</span>
                  {occupancyHint ? (
                    <span class={styles.occupancyHint}>{occupancyHint}</span>
                  ) : (
                    <span class={styles.openHint}>Be the first</span>
                  )}
                </span>
                <span class={styles.optionDescription}>{descriptionFor(option)}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
